
import React from "react";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Quote } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

interface TestimonialProps {
  quote: string;
  name: string;
  role: string;
  imageSrc?: string;
}

const Testimonial = ({ quote, name, role, imageSrc }: TestimonialProps) => {
  return (
    <div className="bg-white rounded-2xl p-6 md:p-8 shadow-lg flex flex-col h-full">
      <div className="flex items-start mb-6">
        <div className="bg-legatia-orange/10 p-2 rounded-full mr-3">
          <Quote className="h-5 w-5 text-legatia-orange" />
        </div>
      </div>
      
      <p className="text-gray-700 italic mb-6 flex-grow">{quote}</p>
      
      <div className="flex items-center">
        <Avatar className="h-12 w-12 border-2 border-legatia-orange/20">
          <AvatarImage src={imageSrc} alt={name} />
          <AvatarFallback className="bg-legatia-orange/20 text-legatia-orange">
            {name.split(" ").map(part => part[0]).join("")}
          </AvatarFallback>
        </Avatar>
        <div className="ml-4">
          <p className="font-bold">{name}</p>
          <p className="text-gray-500 text-sm">{role}</p>
        </div>
      </div>
    </div>
  );
};

const TestimonialsCarousel = () => {
  const { language } = useLanguage();
  
  const testimonialContent = {
    fr: {
      title: "Ils ont créé une capsule temporelle",
      testimonials: [
        {
          quote: "C'est incroyable de voir la réaction de ma fille en découvrant ces messages du passé. Un moment magique.",
          name: "Marie L.",
          role: "Mère de deux enfants",
          imageSrc: "/assets/testimonial-1.jpg"
        },
        {
          quote: "J'ai pu laisser un héritage émotionnel à mon petit-fils. Quelque chose qui dure bien plus qu'un simple cadeau.",
          name: "Jean-Pierre D.",
          role: "Grand-père",
          imageSrc: "/assets/testimonial-2.jpg"
        },
        {
          quote: "Cette capsule temporelle a réuni toute notre famille autour d'un projet plein de sens.",
          name: "Sophie M.",
          role: "Mère de famille",
          imageSrc: "/assets/testimonial-3.jpg"
        }
      ]
    },
    en: {
      title: "They created a capsule",
      testimonials: [
        {
          quote: "It's incredible to see my daughter's reaction when discovering these messages from the past. A magical moment.",
          name: "Marie L.",
          role: "Mother of two",
          imageSrc: "/assets/testimonial-1.jpg"
        },
        {
          quote: "I was able to leave an emotional legacy for my grandson. Something that lasts much longer than a simple gift.",
          name: "Jean-Pierre D.",
          role: "Grandfather",
          imageSrc: "/assets/testimonial-2.jpg"
        },
        {
          quote: "This time capsule brought our whole family together around a meaningful project.",
          name: "Sophie M.",
          role: "Family mother",
          imageSrc: "/assets/testimonial-3.jpg"
        }
      ]
    },
    de: {
      title: "Sie haben eine Kapsel erstellt",
      testimonials: [
        {
          quote: "Es ist unglaublich, die Reaktion meiner Tochter zu sehen, wenn sie diese Nachrichten aus der Vergangenheit entdeckt. Ein magischer Moment.",
          name: "Marie L.",
          role: "Mutter von zwei Kindern",
          imageSrc: "/assets/testimonial-1.jpg"
        },
        {
          quote: "Ich konnte meinem Enkel ein emotionales Erbe hinterlassen. Etwas, das viel länger hält als ein einfaches Geschenk.",
          name: "Jean-Pierre D.",
          role: "Großvater",
          imageSrc: "/assets/testimonial-2.jpg"
        },
        {
          quote: "Diese Zeitkapsel hat unsere ganze Familie um ein sinnvolles Projekt versammelt.",
          name: "Sophie M.",
          role: "Familienmutter",
          imageSrc: "/assets/testimonial-3.jpg"
        }
      ]
    }
  };

  const content = testimonialContent[language];

  return (
    <section className="py-20 md:py-32 bg-gradient-to-br from-gray-50 to-white relative overflow-hidden">
      {/* Background decorations */}
      <div className="absolute top-20 left-[10%] w-64 h-64 bg-gradient-to-r from-legatia-orange/5 to-legatia-yellow/5 rounded-full blur-3xl"></div>
      <div className="absolute bottom-20 right-[15%] w-80 h-80 bg-gradient-to-r from-legatia-yellow/5 to-legatia-green/5 rounded-full blur-3xl"></div>
      
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            {content.title}
          </h2>
        </div>
        
        <div className="max-w-6xl mx-auto">
          <Carousel className="w-full">
            <CarouselContent className="-ml-2 md:-ml-4">
              {content.testimonials.map((testimonial, index) => (
                <CarouselItem key={index} className="pl-2 md:pl-4 md:basis-1/2 lg:basis-1/3">
                  <Testimonial {...testimonial} />
                </CarouselItem>
              ))}
            </CarouselContent>
            <CarouselPrevious className="hidden md:flex" />
            <CarouselNext className="hidden md:flex" />
          </Carousel>
        </div>
      </div>
    </section>
  );
};

export default TestimonialsCarousel;
